"use client";

import { useState } from "react";

import { CompareRowLabel } from "@/components/compareRowLabel/compareRowLabel.component";
import { Link } from "@/components/link/link.component";
import { Select } from "@/components/select/select.component";

import {
  COMPARE_COMPETITOR_TABLE_APPEARANCES,
  COMPARE_COMPETITOR_TABLE_DEFAULTS,
  type CompareCompetitorTableAppearance,
} from "@/sanity/defaults/compareCompetitorTable.defaults";

import { competitorColumnIndexes } from "./compareCompetitorTable.tableUtils";
import type {
  SanityCompareCompetitorTableQueryResult,
  SanityCompetitorCompareColumn,
  SanityCompetitorCompareRow,
} from "./compareCompetitorTable.type";
import { CompareCompetitorTableColumnHeader } from "./compareCompetitorTableColumnHeader.component";
import { competitorTableCtaHref } from "./compareCompetitorTableCtaRow.component";
import { CompareCompetitorTableValue } from "./compareCompetitorTableValue.component";

export const CompareCompetitorTableMobile = ({
  title,
  columns,
  rows,
  highlightIndex,
  appearance,
  ctaLabel,
  ctaLink,
}: CompareCompetitorTableMobileProps) => {
  const competitorIndexes = competitorColumnIndexes(
    columns.length,
    highlightIndex,
  );
  const competitorColumns = competitorIndexes.flatMap((index) => {
    const column = columns[index];
    return column ? [column] : [];
  });
  const [selectedCompetitorId, setSelectedCompetitorId] = useState(
    () => competitorColumns[0]?.id ?? "",
  );

  const highlightedColumn = columns[highlightIndex];
  const selectedCompetitor =
    competitorColumns.find((column) => column.id === selectedCompetitorId) ??
    competitorColumns[0];
  const selectedIndex = columns.findIndex(
    (column) => column.id === selectedCompetitor?.id,
  );
  const ctaHref = competitorTableCtaHref(appearance, ctaLink, highlightIndex);

  if (!highlightedColumn || !selectedCompetitor || selectedIndex < 0) {
    return null;
  }

  const renderRows = () => {
    return rows.map((row) => {
      const cells = row.cells ?? [];

      return (
        <div
          key={row.id}
          className="border-greyscale-strength/20 border-b"
          role="row"
          data-testid={`compare-competitor-table-mobile-row-${row.id}`}>
          <div className="px-4 py-4" role="rowheader">
            <CompareRowLabel
              label={row.label}
              info={row.info}
              variant={
                appearance === COMPARE_COMPETITOR_TABLE_APPEARANCES.adt
                  ? "body5"
                  : "body1"
              }
              className="flex w-full items-center justify-between gap-2"
            />
          </div>
          <div className="grid grid-cols-2 px-2 pb-4">
            <div className="flex items-center justify-center" role="cell">
              <CompareCompetitorTableValue
                cell={cells[highlightIndex]}
                columnIndex={highlightIndex}
                highlightIndex={highlightIndex}
                appearance={appearance}
              />
            </div>
            <div className="flex items-center justify-center" role="cell">
              <CompareCompetitorTableValue
                cell={cells[selectedIndex]}
                columnIndex={selectedIndex}
                highlightIndex={highlightIndex}
                appearance={appearance}
              />
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div
      data-testid="compare-competitor-table-mobile"
      role="table"
      aria-label={title}>
      <div
        className="border-greyscale-strength/20 grid grid-cols-2 items-center gap-2 border-b px-2 pt-1 pb-4"
        role="row">
        <div className="flex items-center justify-center" role="columnheader">
          <CompareCompetitorTableColumnHeader
            column={highlightedColumn}
            columnIndex={highlightIndex}
            highlightIndex={highlightIndex}
            appearance={appearance}
          />
        </div>
        <div className="flex items-center justify-center" role="columnheader">
          <Select
            name="compare-competitor"
            aria-label="Select competitor to compare"
            value={selectedCompetitor.id}
            onChange={(event) => setSelectedCompetitorId(event.target.value)}
            data-testid="compare-competitor-select">
            {competitorColumns.map((column) => (
              <option key={column.id} value={column.id}>
                {column.title}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {renderRows()}

      {ctaHref ? (
        <div className="grid grid-cols-2 px-2 pt-2 pb-6" role="row">
          <div className="flex justify-center" role="cell">
            <Link
              href={ctaHref}
              secondary
              className="inline-flex min-w-38 px-6"
              target={ctaLink?.openInNewTab ? "_blank" : undefined}
              rel={ctaLink?.openInNewTab ? "noopener noreferrer" : undefined}
              data-testid="compare-competitor-table-mobile-cta">
              {ctaLabel?.trim() || COMPARE_COMPETITOR_TABLE_DEFAULTS.ctaLabel}
            </Link>
          </div>
          <div role="cell" />
        </div>
      ) : null}
    </div>
  );
};

type CompareCompetitorTableMobileProps = {
  title: string;
  columns: SanityCompetitorCompareColumn[];
  rows: SanityCompetitorCompareRow[];
  highlightIndex: number;
  appearance: CompareCompetitorTableAppearance;
  ctaLabel: SanityCompareCompetitorTableQueryResult["ctaLabel"];
  ctaLink: SanityCompareCompetitorTableQueryResult["ctaLink"];
};
